import React, { useEffect, useState, useMemo } from 'react';
import api from '../services/api';
import CameraGrid from '../components/CameraGrid';
import CameraModal from '../components/CameraModal';
import CameraAssignmentModal from '../components/CameraAssignmentModal';
import StreamViewer from '../components/StreamViewer';
import NVRMassActions from '../components/NVRMassActions';
import { 
    MapPin, ChevronLeft, Home, Globe, Building, Camera, Filter, 
    ChevronDown, RefreshCw, Monitor, Grid, Eye, Video, 
    Settings, Plus, Edit, Trash2, TestTube, Play, Pause, Clock, ChevronRight, 
    ChevronFirst, ChevronLast 
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Pagination constant
const CAMERAS_PER_PAGE = 20;

const Dashboard = () => {
    const { hasPermission } = useAuth();
    const navigate = useNavigate();

    // Core Data State
    const [loading, setLoading] = useState(true);
    const [allCamerasData, setAllCamerasData] = useState([]); // Master list of all cameras
    const [error, setError] = useState(null);

    // View & Layout State
    const [viewMode, setViewMode] = useState('grid'); // 'grid'
    const [cameraViewType, setCameraViewType] = useState('grid'); // 'list', 'grid' (for CameraGrid component)
    const [matrixLayout, setMatrixLayout] = useState('auto');
    
    // Filter & Pagination State
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedLocation, setSelectedLocation] = useState('ALL');
    const [locationOptions, setLocationOptions] = useState(['ALL']);
    const [currentPage, setCurrentPage] = useState(1);
    
    // Camera Action State
    const [showCameraModal, setShowCameraModal] = useState(false);
    const [editingCamera, setEditingCamera] = useState(null);
    const [showAssignmentModal, setShowAssignmentModal] = useState(false);
    const [selectedCameraForAssignment, setSelectedCameraForAssignment] = useState(null);
    const [streamingCamera, setStreamingCamera] = useState(null);
    const [showStreamViewer, setShowStreamViewer] = useState(false);
    const [thumbnailKeys, setThumbnailKeys] = useState({});
    const [showNVRMassActions, setShowNVRMassActions] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [cameraToDelete, setCameraToDelete] = useState(null);

    // Derived State
    const [notReadyMap, setNotReadyMap] = useState({}); // { [cameraId]: true }
    
    /**
     * Extracts unique location options from the master camera list.
     * Only uses exactLocation field, not city or state.
     */
    const extractLocationOptions = (cameras) => {
        const locations = new Set(['ALL']);
        cameras.forEach(camera => {
            if (camera.location) {
                const { exactLocation } = camera.location;
                if (exactLocation && exactLocation !== 'general') {
                    locations.add(exactLocation.charAt(0).toUpperCase() + exactLocation.slice(1));
                }
            }
        });
        setLocationOptions(Array.from(locations));
    };

    /**
     * Fetches all camera data from the server to populate the master list.
     * This is our single source of truth for all client-side filtering and searching.
     * This is also called by mutation functions (add, edit, delete) to refresh data.
     */
    const loadAllCameraData = async () => {
        try {
            setLoading(true);
            setError(null);
            console.log('Loading all camera data...');
            const response = await api.get('/cameras/mine?limit=1000'); // Load all cameras as per original design
            const cameras = response.data.data.cameras || [];
            
            // Check for duplicate camera IDs in the response
            const cameraIds = cameras.map(cam => cam._id);
            const duplicates = cameraIds.filter((id, index) => cameraIds.indexOf(id) !== index);
            if (duplicates.length > 0) {
                // Remove duplicates by keeping only the first occurrence
                const uniqueCameras = cameras.filter((camera, index, arr) => 
                    arr.findIndex(c => c._id === camera._id) === index
                );
                setAllCamerasData(uniqueCameras);
            } else {
                setAllCamerasData(cameras);
            }
            
            // Extract locations once data is loaded
            extractLocationOptions(cameras);
            
        } catch (error) {
            console.error('Error loading all camera data:', error);
            setError('Failed to load cameras. Please contact support or try again.');
        } finally {
            setLoading(false);
        }
    };

    // Initial data load on component mount
    useEffect(() => {
        loadAllCameraData();
    }, []);

    /**
     * [FIXED] Memoized Filtering Logic.
     * This hook declaratively filters the master camera list based on current search and location state.
     * This is the single source of truth for *filtered* data, fixing the search/filter conflict.
     */
    const filteredCameras = useMemo(() => {
        console.log(`Memo: Re-filtering cameras. Search: "${searchTerm}", Location: "${selectedLocation}"`);
        
        let cameras = allCamerasData;

        // 1. Apply Search Filter
        if (searchTerm && searchTerm.trim() !== '') {
            const searchLower = searchTerm.toLowerCase().trim();
            cameras = cameras.filter(camera => 
                (camera.name || '').toLowerCase().includes(searchLower) ||
                (camera.description || '').toLowerCase().includes(searchLower) ||
                (camera.location?.city || '').toLowerCase().includes(searchLower) ||
                (camera.location?.state || '').toLowerCase().includes(searchLower) ||
                (camera.location?.exactLocation || '').toLowerCase().includes(searchLower) ||
                (camera.ipAddress || '').toLowerCase().includes(searchLower) ||
                (camera.manufacturer || '').toLowerCase().includes(searchLower) ||
                (camera.model || '').toLowerCase().includes(searchLower)
            );
        }

        // 2. Apply Location Filter (on the result of the search filter)
        // Only filter by exactLocation field
        if (selectedLocation !== 'ALL') {
            const locLower = selectedLocation.toLowerCase();
            cameras = cameras.filter(camera =>
                (camera.location?.exactLocation || '').toLowerCase().includes(locLower)
            );
        }

        return cameras;
    }, [allCamerasData, searchTerm, selectedLocation]);

    /**
     * Memoized Pagination Logic.
     * This hook declaratively paginates the *filtered* camera list.
     * This is the final data passed to the grid for rendering.
     */
    const paginatedCameras = useMemo(() => {
        const totalFiltered = filteredCameras.length;
        const startIndex = (currentPage - 1) * CAMERAS_PER_PAGE;
        const endIndex = Math.min(startIndex + CAMERAS_PER_PAGE, totalFiltered);
        
        console.log(`Memo: Re-paginating. Page: ${currentPage}. Slicing [${startIndex}:${endIndex}] from ${totalFiltered} filtered cameras.`);
        
        return filteredCameras.slice(startIndex, endIndex);
    }, [filteredCameras, currentPage]);

    // Derived pagination and stats values
    const totalFilteredCameras = filteredCameras.length;
    const totalPages = Math.ceil(totalFilteredCameras / CAMERAS_PER_PAGE) || 1;
    const hasMoreCameras = currentPage < totalPages;

    const cameraStats = useMemo(() => {
        const online = filteredCameras.filter(c => c.isOnline).length;
        const offline = totalFilteredCameras - online;
        return { total: totalFilteredCameras, online, offline };
    }, [filteredCameras, totalFilteredCameras]);

    // Reset pagination to page 1 whenever filters change
    useEffect(() => {
        if (currentPage !== 1) {
            setCurrentPage(1);
        }
    }, [searchTerm, selectedLocation]); // This ensures filter changes always return to page 1.

    // Prune 'notReady' map to only visible (paginated) cameras
    useEffect(() => {
        const visibleIds = new Set(paginatedCameras.map(c => c._id));
        setNotReadyMap(prev => {
            const next = {};
            Object.keys(prev).forEach(id => {
                if (visibleIds.has(id)) {
                    next[id] = true;
                }
            });
            return next;
        });
    }, [paginatedCameras]);

    // --- Event Handlers (Simplified) ---

    const handleSearchChange = (value) => {
        setSearchTerm(value);
    };

    const handleLocationChange = (location) => {
        setSelectedLocation(location || 'ALL');
    };

    const loadNextPage = () => {
        if (hasMoreCameras) {
            setCurrentPage(prev => prev + 1);
        }
    };

    const loadPreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(prev => prev - 1);
        }
    };

    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };


    const handleStreamStatusChange = (cameraId, status, isOnline) => {
        setNotReadyMap(prev => {
            const next = { ...prev };
            if (status === 'notReady' && isOnline) {
                next[cameraId] = true;
            } else {
                delete next[cameraId];
            }
            return next;
        });
    };

    // --- Mutation Handlers (Now just refresh the master list) ---

    const refreshAllData = () => {
        // This function will re-fetch the master list after any mutation
        loadAllCameraData();
    };

    const toggleCameraStatus = async (cameraId) => {
        try {
            await api.patch(`/cameras/${cameraId}/toggle-status`);
            refreshAllData(); // Refresh master list
        } catch (error) {
            console.error('Error toggling camera status:', error);
        }
    };

    const deleteCamera = (cameraId) => {
        const camera = allCamerasData.find(c => c._id === cameraId);
        setCameraToDelete(camera);
        setShowDeleteDialog(true);
    };

    const confirmDeleteCamera = async () => {
        if (!cameraToDelete) return;
        try {
            await api.delete(`/cameras/${cameraToDelete._id}`);
            refreshAllData(); // Refresh master list
            setShowDeleteDialog(false);
            setCameraToDelete(null);
        } catch (error) {
            console.error('Error deleting camera:', error);
        }
    };

    const cancelDeleteCamera = () => {
        setShowDeleteDialog(false);
        setCameraToDelete(null);
    };

    const handleEditCamera = (camera) => {
        setEditingCamera(camera);
        setShowCameraModal(true);
    };

    const handleAssignCamera = (camera) => {
        setSelectedCameraForAssignment(camera);
        setShowAssignmentModal(true);
    };

    const handleAddCamera = () => {
        setEditingCamera(null);
        setShowCameraModal(true);
    };

    const refreshThumbnail = (cameraId) => {
        const refreshKey = Date.now();
        console.log(`🔄 Dashboard refreshThumbnail called for camera ${cameraId}. New key: ${refreshKey}`);
        setThumbnailKeys(prev => ({ ...prev, [cameraId]: refreshKey }));
    };

    // --- Components ---

    // Action buttons component for CameraGrid
    const getActionButtons = () => {
        if (!hasAnyCamerasAtAll) return null;
        
        return [
            // Live View Button
            hasPermission('cameras', 'read') && (
                <Link
                    key="live-view"
                    to="/camera-monitoring"
                    className="flex items-center space-x-2 px-3 py-2 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white transition-colors text-sm font-medium rounded-lg shadow-sm"
                    title="Live Camera Monitoring"
                >
                    <Eye className="h-4 w-4" />
                    <span>Live View</span>
                </Link>
            ),
            // Add Camera Button
            hasPermission('cameras', 'create') && (
                <button
                    key="add-camera"
                    onClick={handleAddCamera}
                    className="flex items-center space-x-2 px-3 py-2 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white transition-colors text-sm font-medium rounded-lg shadow-sm"
                >
                    <Plus className="h-4 w-4" />
                    <span>Add Camera</span>
                </button>
            ),
            // Location Actions Button
            hasPermission('cameras', 'update') && (
                <button
                    key="location-actions"
                    onClick={() => setShowNVRMassActions(true)}
                    className="flex items-center space-x-2 px-3 py-2 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white transition-colors text-sm font-medium rounded-lg shadow-sm"
                >
                    <Settings className="h-4 w-4" />
                    <span>Location Actions</span>
                </button>
            )
        ].filter(Boolean);
    };

    const CameraStatistics = () => {
        const notReadyCount = Object.keys(notReadyMap).length;
        // Adjust counts only based on the *filtered* list
        const onlineDisplay = Math.max(cameraStats.online - notReadyCount, 0);
        const offlineDisplay = cameraStats.offline + notReadyCount;
        
        return (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-1 mb-1 ">
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm ">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs font-medium text-gray-600">Total Cameras (Filtered)</p>
                            <p className="text-sm font-bold text-gray-900">{cameraStats.total}</p>
                        </div>
                        <div className="p-1 bg-blue-100 rounded-lg">
                            <Camera className="h-3 w-3 text-blue-600" />
                        </div>
                    </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-1 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs font-medium text-gray-600">Online</p>
                            <p className="text-sm font-bold text-green-600">{onlineDisplay}</p>
                        </div>
                        <div className="p-1 bg-green-100 rounded-lg">
                            <Video className="h-3 w-3 text-green-600" />
                        </div>
                    </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-1 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs font-medium text-gray-600">Offline / Connecting</p>
                            <p className="text-sm font-bold text-red-600">{offlineDisplay}</p>
                        </div>
                        <div className="p-1 bg-red-100 rounded-lg">
                            <Monitor className="h-3 w-3 text-red-600" />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // --- Render Logic ---

    if (loading && allCamerasData.length === 0) { // Only show full-page loader on initial load
        return (
            <div className="space-y-4 max-w-full mx-auto px-2 py-4">
                <h2 className="text-xl md:text-2xl font-extrabold text-indigo-700 tracking-tight mb-1">Camera Overview</h2>
                <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-400"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-4 max-w-full mx-auto px-2 py-4">
                <h2 className="text-xl md:text-2xl font-extrabold text-indigo-700 tracking-tight mb-1">Camera Overview</h2>
                <div className="bg-red-50 border border-red-200 p-6 rounded-lg text-center">
                    <Camera className="h-16 w-16 text-red-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-red-900 mb-2">Error Loading Cameras</h3>
                    <p className="text-red-700 mb-4">{error}</p>
                </div>
            </div>
        );
    }
    
    const hasAnyCamerasAtAll = allCamerasData.length > 0;
    const hasFilteredCameras = filteredCameras.length > 0;

    return (
        <div className="max-w-full mx-auto px-4 py-2">



            {/* Main Content Area */}
            {!hasAnyCamerasAtAll ? (
                // State: No cameras exist at all in the account
                <div className="text-center py-12 bg-white border border-gray-200 shadow-sm rounded-lg ">
                    <Camera className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Cameras Available</h3>
                    <p className="text-gray-600 mb-4">No cameras have been added to your account yet.</p>
                    {hasPermission('cameras', 'create') && (
                        <button
                            onClick={handleAddCamera}
                            className="inline-flex items-center space-x-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                        >
                            <Plus className="h-4 w-4" />
                            <span>Add Your First Camera</span>
                        </button>
                    )}
                </div>
            ) : !hasFilteredCameras ? (
                // View: Grid, but no filter results - Show CameraGrid with search interface
                <div>
                    <CameraGrid
                        key={`camera-grid-${matrixLayout}`}
                        cameras={[]} // Pass empty array to show search interface but no results
                        viewMode={cameraViewType}
                        onViewModeChange={setCameraViewType}
                        searchTerm={searchTerm}
                        onSearchChange={handleSearchChange}
                        onStreamStatus={handleStreamStatusChange}
                        onEditCamera={hasPermission('cameras', 'update') ? handleEditCamera : undefined}
                        onDeleteCamera={hasPermission('cameras', 'delete') ? deleteCamera : undefined}
                        onToggleStatus={hasPermission('cameras', 'update') ? toggleCameraStatus : undefined}
                        onAssignCamera={hasPermission('cameras', 'update') ? handleAssignCamera : undefined}
                        matrixLayout={matrixLayout}
                        onMatrixLayoutChange={setMatrixLayout}
                        hasPlaybackPermission={hasPermission('cameras', 'playback')}
                        thumbnailKeys={thumbnailKeys}
                        // Location Filter Props
                        selectedLocation={selectedLocation}
                        locationOptions={locationOptions}
                        onLocationChange={handleLocationChange}
                        // Action buttons
                        actionButtons={getActionButtons()}
                    />
                </div>
            ) : (
                // View: Grid with results
                <div>
                    <CameraGrid
                        key={`camera-grid-${matrixLayout}`}
                        cameras={paginatedCameras} // Pass the paginated subset
                        viewMode={cameraViewType}
                        onViewModeChange={setCameraViewType}
                        searchTerm={searchTerm}
                        onSearchChange={handleSearchChange}
                        onStreamStatus={handleStreamStatusChange}
                        onEditCamera={hasPermission('cameras', 'update') ? handleEditCamera : undefined}
                        onDeleteCamera={hasPermission('cameras', 'delete') ? deleteCamera : undefined}
                        onToggleStatus={hasPermission('cameras', 'update') ? toggleCameraStatus : undefined}
                        onAssignCamera={hasPermission('cameras', 'update') ? handleAssignCamera : undefined}
                        matrixLayout={matrixLayout}
                        onMatrixLayoutChange={setMatrixLayout}
                        hasPlaybackPermission={hasPermission('cameras', 'playback')}
                        thumbnailKeys={thumbnailKeys}
                        // Location Filter Props
                        selectedLocation={selectedLocation}
                        locationOptions={locationOptions}
                        onLocationChange={handleLocationChange}
                        // Action buttons
                        actionButtons={getActionButtons()}
                    />

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="mt-6 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                            {/* Pagination UI */}
                            <div className="flex flex-col md:flex-row items-center justify-between p-4 gap-4">
                                {/* Info */}
                                <div className="text-sm text-gray-700">
                                    Showing <span className="font-medium">{((currentPage - 1) * CAMERAS_PER_PAGE) + 1}</span> to <span className="font-medium">{Math.min(currentPage * CAMERAS_PER_PAGE, totalFilteredCameras)}</span> of <span className="font-medium">{totalFilteredCameras}</span> results
                                </div>

                                {/* Controls */}
                                <div className="flex items-center space-x-1">
                                    <button
                                        onClick={() => goToPage(1)}
                                        disabled={currentPage === 1 || loading}
                                        className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-md hover:bg-gray-100 transition-colors"
                                        title="First Page"
                                    >
                                        <ChevronFirst className="h-4 w-4" />
                                    </button>
                                    <button
                                        onClick={loadPreviousPage}
                                        disabled={currentPage === 1 || loading}
                                        className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-md hover:bg-gray-100 transition-colors"
                                        title="Previous Page"
                                    >
                                        <ChevronLeft className="h-4 w-4" />
                                    </button>

                                    {/* Page Info (Mobile) */}
                                    <span className="md:hidden px-3 py-2 text-sm font-medium text-gray-700">
                                        Page {currentPage} of {totalPages}
                                    </span>

                                    {/* Page Numbers (Desktop) */}
                                    <div className="hidden md:flex items-center space-x-1 mx-2">
                                        {/* Pagination Number Logic (Example) */}
                                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                            let pageNum;
                                            if (totalPages <= 5) pageNum = i + 1;
                                            else if (currentPage <= 3) pageNum = i + 1;
                                            else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                                            else pageNum = currentPage - 2 + i;

                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => goToPage(pageNum)}
                                                    disabled={loading}
                                                    className={`px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                                                        currentPage === pageNum
                                                            ? 'bg-indigo-600 text-white shadow-sm'
                                                            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                                                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    
                                    <button
                                        onClick={loadNextPage}
                                        disabled={!hasMoreCameras || loading}
                                        className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-md hover:bg-gray-100 transition-colors"
                                        title="Next Page"
                                    >
                                        <ChevronRight className="h-4 w-4" />
                                    </button>
                                    <button
                                        onClick={() => goToPage(totalPages)}
                                        disabled={currentPage === totalPages || loading}
                                        className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-md hover:bg-gray-100 transition-colors"
                                        title="Last Page"
                                    >
                                        <ChevronLast className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
            

            {/* --- Modals --- */}
            
            <CameraModal
                isOpen={showCameraModal}
                onClose={() => {
                    setShowCameraModal(false);
                    setEditingCamera(null);
                }}
                camera={editingCamera}
                onSuccess={(updatedCamera) => {
                    console.log(`📸 Dashboard: Camera mutation success, refreshing master data.`);
                    setShowCameraModal(false);
                    setEditingCamera(null);
                    refreshAllData(); // Refresh master list

                    const camId = updatedCamera?._id || editingCamera?._id;
                    if (camId) {
                        refreshThumbnail(camId);
                        setTimeout(() => refreshThumbnail(camId), 1000); // Secondary refresh
                    }
                }}
            />

            <CameraAssignmentModal
                isOpen={showAssignmentModal}
                onClose={() => {
                    setShowAssignmentModal(false);
                    setSelectedCameraForAssignment(null);
                }}
                camera={selectedCameraForAssignment}
                onSuccess={() => {
                    setShowAssignmentModal(false);
                    setSelectedCameraForAssignment(null);
                    refreshAllData(); // Refresh master list (assignments might change 'mine')
                }}
            />

            <StreamViewer
                isOpen={showStreamViewer}
                onClose={() => {
                    setShowStreamViewer(false);
                    setStreamingCamera(null);
                }}
                camera={streamingCamera}
            />

            {showNVRMassActions && (
                <NVRMassActions
                    onSuccess={() => {
                        setShowNVRMassActions(false);
                        refreshAllData(); // Refresh master list
                    }}
                    onClose={() => setShowNVRMassActions(false)}
                    hasUpdatePermission={hasPermission('cameras', 'update')}
                    hasDeletePermission={hasPermission('cameras', 'delete')}
                    onEditIndividualCamera={handleEditCamera}
                />
            )}

            {showDeleteDialog && cameraToDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
                        <div className="flex items-start">
                            <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-red-100 sm:w-12 sm:h-12">
                                <Trash2 className="w-5 h-5 text-red-600 sm:w-6 sm:h-6" aria-hidden="true" />
                            </div>
                            <div className="ml-4 flex-1">
                                <h3 className="text-lg font-medium text-gray-900" id="modal-title">
                                    Delete Camera
                                </h3>
                                <div className="mt-2">
                                    <p className="text-sm text-gray-600">
                                        Are you sure you want to delete <strong>"{cameraToDelete.name}"</strong>? This action will permanently remove the camera and cannot be undone.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse sm:gap-3">
                            <button
                                type="button"
                                className="inline-flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:text-sm"
                                onClick={confirmDeleteCamera}
                            >
                                Delete Camera
                            </button>
                            <button
                                type="button"
                                className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:text-sm"
                                onClick={cancelDeleteCamera}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dashboard;